/**
 * RescheduleService - Reagendamiento de consultas para pacientes existentes.
 *
 * Usa la RPC reschedule_patient_consultation (SECURITY DEFINER) para bypassear
 * RLS — el rol anon no tiene acceso directo a SELECT/UPDATE en consultas.
 * La RPC verifica ownership via RUT del paciente.
 *
 * Prerrequisito en Supabase: ejecutar
 *   sql/migrations/fix_patient_booking_ops_2026_05_15.sql
 */

import { supabase } from '../lib/supabase/client';
import { toSantiagoISO } from '../lib/utils/dateUtils';
import { EmailService } from './email.service';

export interface RescheduleData {
  consultationId:  string;
  newDate:         string; // YYYY-MM-DD
  newTime:         string; // HH:MM (hora local de Santiago)
  patientRut:      string; // requerido para verificación de ownership en RPC
  therapistId?:    string;
  patientName?:    string;
  patientEmail?:   string;
  therapistName?:  string;
  therapistEmail?: string;
  oldDateTime?:    string; // ISO — para la notificación de email
}

export interface RescheduleResult {
  success:         boolean;
  consultationId?: string;
  oldDateTime?:    string;
  newDateTime?:    string;
  error?:          string;
}

export class RescheduleService {
  /**
   * Reagendar una consulta existente via RPC SECURITY DEFINER.
   *
   * El proceso anterior usaba acceso directo a la tabla consultas, lo cual
   * falla para el rol anon por RLS. Ahora delega toda la lógica de DB a la
   * RPC reschedule_patient_consultation que corre como postgres.
   */
  static async rescheduleConsultation(data: RescheduleData): Promise<RescheduleResult> {
    const {
      consultationId,
      newDate,
      newTime,
      patientRut,
      patientName,
      patientEmail,
      therapistName,
      therapistEmail,
      oldDateTime,
    } = data;

    if (!patientRut) {
      return { success: false, error: 'RUT del paciente requerido para reagendar' };
    }

    const newDateTime = toSantiagoISO(newDate, newTime);

    const { error } = await supabase.rpc('reschedule_patient_consultation', {
      p_consultation_id: consultationId,
      p_patient_rut:     patientRut,
      p_new_fecha_hora:  newDateTime,
    });

    if (error) {
      console.error('Error en reschedule_patient_consultation:', error.message);
      const userMessage = error.message.includes('no está disponible')
        ? 'El nuevo horario no está disponible. Elegí otro.'
        : error.message.includes('24 horas')
        ? 'No se puede reagendar con menos de 24 horas de anticipación.'
        : 'No se pudo reagendar la consulta. Intentá de nuevo.';
      return { success: false, error: userMessage };
    }

    // Notificación por email (no bloquea si falla)
    // FUNC-126: sin patientEmail (paciente con email en ficha que no pasó por
    // el gate) el Edge Function resuelve el destinatario con consultaId + RUT.
    if ((patientEmail || patientRut) && patientName && therapistName) {
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const oldDT = oldDateTime ? new Date(oldDateTime) : null;
      try {
        await EmailService.sendRescheduleNotification({
          patientName,
          ...(patientEmail ? { patientEmail } : { consultaId: consultationId, patientRut }),
          therapistName,
          therapistEmail: therapistEmail ?? 'angeldiazdiaz01@gmail.com',
          oldDate: oldDT
            ? `${oldDT.getFullYear()}-${pad2(oldDT.getMonth() + 1)}-${pad2(oldDT.getDate())}`
            : newDate,
          oldTime: oldDT
            ? `${pad2(oldDT.getHours())}:${pad2(oldDT.getMinutes())}`
            : newTime,
          newDate,
          newTime,
          modality: 'presencial',
        });
      } catch {
        // La notificación falla silenciosamente — el reagendamiento ya ocurrió
      }
    }

    return {
      success:       true,
      consultationId,
      newDateTime,
    };
  }
}
